import { useState, useCallback, useEffect } from "react";
import Modal from "react-responsive-modal";
import Cropper from "react-easy-crop";

import style from "./style.module.scss";
import { Button, Slider } from "../../../ui";
import {
  cropToTransform,
  type PhotoTransform,
  type CropArea,
  type CropPosition,
} from "../../utils/photo-transform";

interface ImageEditorProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  initialTransform: PhotoTransform | null;
  aspectRatio?: number;
  cropShape?: "rect" | "round";
  onSave: (transform: PhotoTransform) => void;
}

function ImageEditor({
  open,
  onClose,
  imageUrl,
  initialTransform,
  aspectRatio = 1,
  cropShape = "rect",
  onSave,
}: ImageEditorProps) {
  const [crop, setCrop] = useState<CropPosition>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPercentages, setCroppedAreaPercentages] = useState<CropArea | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCrop(initialTransform?.cropPosition || { x: 0, y: 0 });
      setZoom(initialTransform?.zoom || 1);
      setCroppedAreaPercentages(null);
    }
  }, [open, initialTransform]);

  const onCropComplete = useCallback((croppedArea: CropArea) => {
    setCroppedAreaPercentages(croppedArea);
  }, []);

  const handleSave = () => {
    if (croppedAreaPercentages) {
      const transform = cropToTransform(croppedAreaPercentages, zoom, crop);
      onSave(transform);
    }
    onClose();
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <Modal open={open} onClose={onClose} center classNames={{ modal: style.modal }}>
      <div className={style.container}>
        <h3 className={style.title}>Ajuster l'image</h3>

        <div className={style.cropperContainer}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={cropShape}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            minZoom={1}
            maxZoom={3}
            restrictPosition={true}
          />
        </div>

        <div className={style.controls}>
          <label className={style.zoomLabel}>
            Zoom
            <Slider min={1} max={3} step={0.1} value={zoom} onChange={setZoom} />
          </label>
        </div>

        <div className={style.actions}>
          <Button onClick={handleReset} isSecondary>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} isPrimary>
            Appliquer
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ImageEditor;
